import { useAuth0 } from "@auth0/auth0-react";
import { Box } from "@chakra-ui/react/box";
import { Button } from "@chakra-ui/react/button";
import { Drawer } from "@chakra-ui/react/drawer";
import { Field } from "@chakra-ui/react/field";
import { useBreakpointValue } from "@chakra-ui/react/hooks";
import { Icon } from "@chakra-ui/react/icon";
import { PinInput } from "@chakra-ui/react/pin-input";
import { Spinner } from "@chakra-ui/react/spinner";
import { HStack, VStack } from "@chakra-ui/react/stack";
import { Text } from "@chakra-ui/react/text";
import { toaster } from "components/ui/toaster";
import { OtpDTO } from "entities/dto/AuthorizationDTO";
import { useCurrentAgent } from "hooks/queries/use-current-agent";
import {
  useAcceptOtp,
  useChangePassword,
  useSendOtp,
} from "hooks/queries/use-password-change";
import React, { useEffect, useState } from "react";
import { LuCheck } from "react-icons/lu";
import { useAppSelector } from "redux/hooks";
import { AcceptOtpCommand, SendOtpCommand } from "util/authorization";

interface PasswordChangeConfirmationProps {
  newPassword: string;
  isOpen: boolean;
  onClose: () => void;
}

const PasswordChangeConfirmation = ({
  newPassword,
  isOpen = false,
  onClose,
}: PasswordChangeConfirmationProps) => {
  const { colorScheme } = useAppSelector((state) => state.theme);
  const { data: currentAgent } = useCurrentAgent();

  // TanStack Query mutations
  const sendOtpMutation = useSendOtp();
  const acceptOtpMutation = useAcceptOtp();
  const changePasswordMutation = useChangePassword();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resendCodeEnabled, setResendCodeEnabled] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(true);

  const [otpChannel, setOtpChannel] = useState<"email" | "phone" | null>(null);
  const [otpCode, setOtpCode] = useState<string[]>(["", "", "", "", "", ""]);
  const [otpId, setOtpId] = useState<string | null>(null);
  const [otpStatus, setOtpStatus] = useState<"active" | "accepted" | "invalid">(
    "active"
  );

  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );

  const changePassword = async () => {
    if (!otpId) return;

    setSubmitting(true);
    const acceptOtpCommand = {
      referenceId: currentAgent!.id.toString(),
      flow: "password_change",
      code: otpCode.join(""),
    } as AcceptOtpCommand;

    try {
      let result;
      if (otpStatus !== "accepted" && otpChannel === "email") {
        result = await acceptOtpMutation.mutateAsync({
          ...acceptOtpCommand,
          channel: "email",
          handle: currentAgent!.email,
        });
      } else if (
        otpStatus !== "accepted" &&
        currentAgent!.notificationConfig?.handle
      ) {
        result = await acceptOtpMutation.mutateAsync({
          ...acceptOtpCommand,
          channel: "sms",
          handle: currentAgent!.notificationConfig?.handle,
        });
      } else {
        result = ["success", { id: otpId } as OtpDTO];
      }

      if (result[0] === "error") {
        setOtpStatus("invalid");
        toaster.create({
          type: "error",
          title: "Invalid OTP code, please try again.",
        });
        setSubmitting(false);
        return;
      }

      setOtpStatus("accepted");
      const changePasswordResult = await changePasswordMutation.mutateAsync({
        otpId,
        newPassword,
      });

      if (changePasswordResult[0] === "error") {
        toaster.create({
          type: "error",
          title: "Could not change password. Please try again.",
        });
      } else {
        toaster.create({
          type: "success",
          title: "Password changed successfully.",
        });
        onClose();
      }
    } catch (error) {
      toaster.create({
        type: "error",
        title: "An error occurred. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const sendOtp = async () => {
    setResendCodeEnabled(false);
    setResendingOtp(true);

    const sendOtpCommand = {
      referenceId: currentAgent!.id.toString(),
      flow: "password_change",
    } as SendOtpCommand;

    try {
      let result;
      if (currentAgent!.email) {
        setOtpChannel("email");
        result = await sendOtpMutation.mutateAsync({
          ...sendOtpCommand,
          channel: "email",
          handle: currentAgent!.email,
        });
      } else if (currentAgent!.notificationConfig?.handle) {
        setOtpChannel("phone");
        result = await sendOtpMutation.mutateAsync({
          ...sendOtpCommand,
          channel: "sms",
          handle: currentAgent!.notificationConfig?.handle,
        });
      } else {
        setResendingOtp(false);
        return;
      }

      if (result && result[0] === "error") {
        setOtpStatus("invalid");
        setResendCodeEnabled(true);
      } else if (result && result[0] === "success") {
        setOtpStatus("active");
        setOtpId(result[1].id);
      }
    } catch (error) {
      setOtpStatus("invalid");
      setResendCodeEnabled(true);
    } finally {
      setResendingOtp(false);
    }
  };

  const initialisePasswordConfirmation = async () => {
    await sendOtp();

    setLoading(false);
  };

  useEffect(() => {
    if (!isOpen || resendCodeEnabled) return;

    const timeout = setTimeout(() => {
      setResendCodeEnabled(true);
    }, 30000);

    // eslint-disable-next-line consistent-return
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isOpen, resendCodeEnabled]);

  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    setOtpCode(["", "", "", "", "", ""]);
    initialisePasswordConfirmation();
  }, [isOpen]);

  return (
    <Drawer.Root
      open={isOpen}
      placement="bottom"
      closeOnEscape={false}
      closeOnInteractOutside={false}
      onOpenChange={({ open: newIsOpen }) => {
        if (!newIsOpen) {
          onClose();
        }
      }}
    >
      <Drawer.Backdrop />
      <Drawer.Positioner
        zIndex={99999999} // pushes it in front of the change password modal
      >
        <Drawer.Content
          borderTopRadius={isBaseSize ? "0" : "3xl"}
          maxW="560px !important"
          borderBottomRadius="0 !important"
          mx="auto"
        >
          <Drawer.CloseTrigger />
          <Drawer.Header>Confirm your identity</Drawer.Header>
          {loading && (
            <Box minH="160px" display="flex" alignItems="center">
              <Spinner />
            </Box>
          )}
          {!loading && (
            <>
              <Drawer.Body>
                <VStack gap={4} display="flex">
                  <Field.Root
                    invalid={otpStatus === "invalid"}
                    alignItems="center"
                  >
                    <Field.Label>
                      Please provide the code sent to your {otpChannel}
                    </Field.Label>
                    <PinInput.Root
                      otp
                      value={otpCode}
                      onValueChange={(e) => {
                        setOtpCode(e.value);
                      }}
                      autoFocus={true}
                    >
                      <PinInput.HiddenInput />
                      <PinInput.Control>
                        <PinInput.Input index={0} />
                        <PinInput.Input index={1} />
                        <PinInput.Input index={2} />
                        <PinInput.Input index={3} />
                        <PinInput.Input index={4} />
                        <PinInput.Input index={5} />
                      </PinInput.Control>
                    </PinInput.Root>
                    <Field.ErrorText>
                      Invalid OTP code, please try again.
                    </Field.ErrorText>
                  </Field.Root>
                </VStack>
              </Drawer.Body>
              <Drawer.Footer justifyContent="space-between" gap={2}>
                <Button
                  colorPalette={colorScheme}
                  variant="ghost"
                  onClick={resendCodeEnabled ? () => sendOtp() : () => {}}
                  cursor={resendCodeEnabled ? "pointer" : "default"}
                  opacity={resendCodeEnabled ? 1 : 0}
                  transition="opacity 3s ease-in-out"
                >
                  <HStack w={isBaseSize ? "105px" : "220px"} justify="center">
                    <Text>
                      {isBaseSize
                        ? "Resend Code"
                        : "Haven't received the code?"}
                    </Text>
                    {resendingOtp && (
                      <Spinner
                        position="absolute"
                        right="10px"
                        width="10px"
                        height="10px"
                      />
                    )}
                    {!resendingOtp && !resendCodeEnabled && (
                      <Icon as={LuCheck} position="absolute" right="10px" />
                    )}
                  </HStack>
                </Button>
                <HStack gap={2}>
                  <Button colorPalette="gray" variant="ghost" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    colorPalette={colorScheme}
                    onClick={changePassword}
                    loading={submitting}
                  >
                    Save
                  </Button>
                </HStack>
              </Drawer.Footer>
            </>
          )}
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
};

export default PasswordChangeConfirmation;
